import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Button, ButtonToggles, Input, TimeMaskedInput } from 'components';
import { HTTPStatusCode } from 'config/const';
import { Appearance } from 'design/types';
import validations from 'lib/Validations';
import Form from 'screens/Error/Form';
import { selectAPIRequestErrors, selectIsLoadingAPIRequest } from 'store/ApiClient';
import { createDaySegmentRequest, DaySegments, updateDaySegmentRequest } from 'store/Collections/DaySegments';
import { IStore } from 'store/types';
import { dayOptions, IApiErrorPayloadWithCode, ICreateUpdateDaySegmentRequest, IDaySegment } from 'types';
import { stripSpaces } from 'utils/strings';
import { SettingsLayout, SettingsSection } from '../../styles';
import { ActionSection, StyledFieldWrapper, StyledTag } from './styles';

interface IDaySegmentFormProps {
  siteId: string;
  daySegment?: IDaySegment;
  onClose?(): void;
}

export const DaySegmentForm: React.FC<IDaySegmentFormProps> = ({ siteId, daySegment, onClose }) => {
  const dispatch = useDispatch();
  const actionId = daySegment ? DaySegments.UPDATE_DAY_SEGMENT_REQUEST : DaySegments.CREATE_DAY_SEGMENT_REQUEST;
  const isLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, actionId));
  const daySegmentError = useSelector((state: IStore) => selectAPIRequestErrors(state, actionId)) as IApiErrorPayloadWithCode;

  const validationSchema = Yup.object().shape({
    name: Yup.string().trim().required('You must enter a name for the day segment').max(50, 'Name must be at most 50 characters'),
    days: Yup.array().of(Yup.string()).min(1, 'At least one day must be selected'),
    start: validations.timeValidationSchema,
    end: validations.timeValidationSchema.test('time-match', 'Cannot match start', (value, ctx) => ctx.parent.start !== value),
  });

  const { dirty, errors, touched, getFieldProps, handleSubmit, setFieldValue, values } = useFormik({
    initialValues: daySegment || {
      name: '',
      days: [],
      start: '',
      end: '',
    },
    onSubmit: (vals: ICreateUpdateDaySegmentRequest) => {
      const values = { ...vals, name: stripSpaces(vals.name) };
      if (daySegment) {
        dispatch(updateDaySegmentRequest(siteId, daySegment.id, values, onClose));
      } else {
        dispatch(createDaySegmentRequest(siteId, values, onClose));
      }
    },
    validateOnBlur: true,
    validationSchema,
    enableReinitialize: true,
  });

  const endsNextDay = useMemo(() => {
    let endsNextDay = false;

    if (values.start && values.end && !errors.start && !errors.end) {
      const date = dayjs().format('YYYY-MM-DD');
      const startDate = dayjs(`${date} ${values.start}`);
      const endDate = dayjs(`${date} ${values.end}`);

      endsNextDay = endDate <= startDate;
    }

    return endsNextDay;
  }, [values, errors]);

  const validateField = (field: 'name' | 'days' | 'start' | 'end') => Boolean(touched[field] && errors[field]);

  return (
    <SettingsLayout data-cy="day-segment-form">
      {daySegmentError?.statusCode === HTTPStatusCode.CONFLICT && <StyledTag appearance={Appearance.DANGER} label="Day segments names must be unique" dataCy="name-error" />}
      {daySegmentError?.statusCode === HTTPStatusCode.UNPROCESSABLE_ENTITY && <StyledTag appearance={Appearance.DANGER} label="Day segments cannot overlap" dataCy="overlap-error" />}
      <Form formId={actionId} onSubmit={handleSubmit}>
        <StyledFieldWrapper label="Day Segment Name" data-cy="name" message={validateField('name') ? errors.name : ''} hasError={validateField('name')}>
          <Input {...getFieldProps('name')} maxLength={50} />
        </StyledFieldWrapper>

        <SettingsSection>
          <StyledFieldWrapper label="Days" data-cy="days" message={validateField('days') ? (errors.days as string) : ''} hasError={validateField('days')}>
            <ButtonToggles values={getFieldProps('days').value} options={dayOptions} onChange={(val) => setFieldValue('days', val)} />
          </StyledFieldWrapper>
          <StyledFieldWrapper label="Start Time" data-cy="start" message={validateField('start') ? errors.start : ''} hasError={validateField('start')} width={115}>
            <TimeMaskedInput {...getFieldProps('start')} onChange={(val) => setFieldValue('start', val)} />
          </StyledFieldWrapper>
          <StyledFieldWrapper
            label="End Time"
            data-cy="end"
            message={validateField('end') ? errors.end : ''}
            hasError={validateField('end')}
            width={115}
            icon={endsNextDay ? 'CalendarPlus' : undefined}
            tooltipIcon="This shift ends on the next calendar day"
          >
            <TimeMaskedInput {...getFieldProps('end')} onChange={(val) => setFieldValue('end', val)} />
          </StyledFieldWrapper>
        </SettingsSection>

        <ActionSection>
          <Button appearance="white" label="Cancel" onClick={onClose} disabled={isLoading} />
          <Button type="submit" label="Save Day Segment" isProcessing={isLoading} disabled={isLoading || !dirty} />
        </ActionSection>
      </Form>
    </SettingsLayout>
  );
};
